// src/pages/DeviceLinking/index.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  TextField,
  Autocomplete,
  Chip,
  Divider,
  Paper,
  LinearProgress,
  Tooltip,
  InputAdornment,
  Skeleton,
  ToggleButtonGroup,
  ToggleButton,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Link as LinkIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Lightbulb as SuggestionIcon,
  DevicesOther as DeviceIcon,
  LocationOn as LocationIcon,
  Warning as WarningIcon,
  Business as SiteIcon,
  MeetingRoom as UnitIcon,
  PlayArrow as PlayIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  SelectAll as SelectAllIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeSettings } from '../../contexts/ThemeContext';
import DeviceService from '../../services/deviceService';
import LocationService from '../../services/locationService';
import { devicesApi } from '../../services/apiClient';

/**
 * Extract tokens from a string for matching
 * Handles patterns like:
 * - "SH11 - 14/0508" -> ["sh11", "14", "05", "08"]
 * - "SH13 3.401" -> ["sh13", "3", "03", "4", "04", "01", "401"]
 */
const extractTokens = (str) => {
  if (!str) return [];

  const s = str.toLowerCase().trim();

  // First, handle decimal notation like "3.401" before splitting
  // This pattern means: building.floor+room (e.g., 3.401 = building 3, floor 4, room 01)
  const decimalPattern = /(\d+)\.(\d+)/g;
  let processedStr = s;
  const decimalMatches = [...s.matchAll(decimalPattern)];

  const tokens = [];

  for (const match of decimalMatches) {
    const beforeDot = match[1]; // e.g., "3"
    const afterDot = match[2];  // e.g., "401" or "07"

    // Add the before-dot number with and without leading zero
    // For "3.401": 3 = building
    // For "3.07": 3 = floor
    tokens.push(beforeDot);
    if (beforeDot.length === 1) {
      tokens.push('0' + beforeDot); // "3" -> "03"
    }

    // Parse the after-dot part based on length
    if (afterDot.length === 3) {
      // Pattern: X + YY (floor + room) e.g., "401" -> floor 4, room 01
      // For "3.401": building 3, floor 4, room 01
      tokens.push(afterDot[0]);           // "4"
      tokens.push('0' + afterDot[0]);     // "04"
      tokens.push(afterDot.substring(1)); // "01"
    } else if (afterDot.length === 4) {
      // Pattern: XX + YY (floor + room) e.g., "0401" -> "04", "01"
      tokens.push(afterDot.substring(0, 2)); // "04"
      tokens.push(afterDot.substring(2, 4)); // "01"
    } else if (afterDot.length === 2) {
      // Pattern: YY (room only) e.g., "07" -> room 07
      // For "3.07": floor 3, room 07
      tokens.push(afterDot); // "07"
      // Also add without leading zero if applicable
      if (afterDot.startsWith('0')) {
        tokens.push(afterDot.substring(1)); // "07" -> "7"
      }
    } else if (afterDot.length === 1) {
      // Single digit room e.g., "3.7" -> floor 3, room 7
      tokens.push(afterDot);
      tokens.push('0' + afterDot); // "7" -> "07"
    }
    tokens.push(afterDot); // Also add the full after-dot part
  }

  // Split by common separators (but keep the original string processing)
  const parts = s.split(/[\s\-_.,/\\]+/).filter(p => p.length > 0);

  for (const part of parts) {
    // Add the part itself
    tokens.push(part);

    // If it's a 4-digit number, split it into 2+2 (e.g., "0508" -> "05", "08")
    if (/^\d{4}$/.test(part)) {
      tokens.push(part.substring(0, 2));
      tokens.push(part.substring(2, 4));
    }

    // If it's a 3-digit number, try 1+2 split (e.g., "401" -> "4", "04", "01")
    if (/^\d{3}$/.test(part)) {
      tokens.push(part[0]);              // "4"
      tokens.push('0' + part[0]);        // "04"
      tokens.push(part.substring(1));    // "01"
    }

    // If it's a 1-digit number, also add with leading zero
    if (/^\d$/.test(part)) {
      tokens.push('0' + part); // "3" -> "03"
    }

    // If it's a 2-digit number starting with 0, also add without leading zero
    if (/^0\d$/.test(part)) {
      tokens.push(part[1]); // "03" -> "3"
    }

    // Extract alphanumeric codes (e.g., "F05" -> "f05", "05", "5")
    // DON'T extract single letter prefixes separately - "sh" from "sh16" is not meaningful alone
    const alphaMatch = part.match(/^([a-z]+)(\d+)$/);
    if (alphaMatch) {
      const letterPart = alphaMatch[1];
      const numPart = alphaMatch[2];

      // Only extract number part for floor codes (single letter like "f")
      // For site codes (like "sh16"), keep them as atomic units
      if (letterPart.length === 1) {
        // Floor code like "f05" - extract number for matching
        tokens.push(numPart); // "05"
        if (numPart.startsWith('0')) {
          tokens.push(numPart.replace(/^0+/, '')); // "05" -> "5"
        } else if (numPart.length === 1) {
          tokens.push('0' + numPart); // "5" -> "05"
        }
      }
      // Don't extract "sh" or "16" separately from "sh16" - it causes false matches
    }

    // Extract number-alpha codes (e.g., "14a" -> "14", "a")
    const numAlphaMatch = part.match(/^(\d+)([a-z]+)$/);
    if (numAlphaMatch) {
      tokens.push(numAlphaMatch[1]);
      tokens.push(numAlphaMatch[2]);
    }
  }

  // Remove duplicates and empty tokens
  return [...new Set(tokens)].filter(t => t.length >= 1);
};

/**
 * Calculate similarity score between two strings (0-1)
 * Uses token-based matching optimized for device/location naming patterns
 * HEAVILY prioritizes MORE SPECIFIC locations (more matching tokens in absolute terms)
 */
const calculateSimilarity = (deviceName, locationName) => {
  if (!deviceName || !locationName) return 0;

  const d = deviceName.toLowerCase().trim();
  const l = locationName.toLowerCase().trim();

  // Exact match
  if (d === l) return 1;

  // Extract tokens from both strings
  const deviceTokens = extractTokens(deviceName);
  const locationTokens = extractTokens(locationName);

  if (deviceTokens.length === 0 || locationTokens.length === 0) return 0;

  // Filter out single-letter tokens and brand names for meaningful matching
  const meaningfulDeviceTokens = deviceTokens.filter(t =>
    t.length > 1 && !['bode', 'caro', 'device', 'hub', 'sensor'].includes(t)
  );
  const meaningfulLocationTokens = locationTokens.filter(t => t.length > 1);

  // Count matches - a token matches if it equals or is contained in another
  const tokenMatches = (t1, t2) => {
    if (t1 === t2) return true;

    // Normalize numbers by stripping leading zeros for comparison
    const normalize = (s) => /^\d+$/.test(s) ? (s.replace(/^0+/, '') || '0') : s;
    const isPureNumber = (s) => /^\d+$/.test(s);
    const isFloorCode = (s) => /^f\d+$/i.test(s);
    const isSiteCode = (s) => /^[a-z]{2,}\d+$/i.test(s); // sh16, sh10, etc.

    // Pure number comparison with normalization (handles "05" == "5")
    if (isPureNumber(t1) && isPureNumber(t2)) {
      return normalize(t1) === normalize(t2);
    }

    // Site codes (sh16, sh10) must match EXACTLY - no substring matching
    if (isSiteCode(t1) || isSiteCode(t2)) {
      return t1 === t2;
    }

    // Floor codes (f05, f5) - match if floor numbers are equal
    if (isFloorCode(t1) && isPureNumber(t2)) {
      const floorNum = t1.replace(/^f/i, '');
      return normalize(floorNum) === normalize(t2);
    }
    if (isFloorCode(t2) && isPureNumber(t1)) {
      const floorNum = t2.replace(/^f/i, '');
      return normalize(floorNum) === normalize(t1);
    }
    if (isFloorCode(t1) && isFloorCode(t2)) {
      const num1 = t1.replace(/^f/i, '');
      const num2 = t2.replace(/^f/i, '');
      return normalize(num1) === normalize(num2);
    }

    // NO general substring matching - it causes too many false positives
    // e.g., "102" containing "10" should NOT match
    return false;
  };

  // Count how many meaningful location tokens match device tokens
  let matchedCount = 0;
  const matchedTokensList = [];

  for (const lt of meaningfulLocationTokens) {
    for (const dt of meaningfulDeviceTokens) {
      if (tokenMatches(lt, dt)) {
        matchedCount++;
        matchedTokensList.push(lt);
        break;
      }
    }
  }

  // Also count how many device tokens are matched by location tokens
  // This helps identify when a location is missing important device info
  let deviceTokensMatched = 0;
  for (const dt of meaningfulDeviceTokens) {
    for (const lt of meaningfulLocationTokens) {
      if (tokenMatches(dt, lt)) {
        deviceTokensMatched++;
        break;
      }
    }
  }

  // Key insight: we want the location that matches the MOST tokens from the device
  // The ABSOLUTE number of matches is the PRIMARY factor

  // Primary score: based on absolute matches (0.15 per match, up to 0.9 for 6 matches)
  const absoluteScore = Math.min(matchedCount * 0.15, 0.9);

  // Secondary score: bonus if device tokens are well-covered by location
  const deviceCoverage = deviceTokensMatched / meaningfulDeviceTokens.length;
  const coverageBonus = deviceCoverage * 0.1;

  let score = absoluteScore + coverageBonus;

  // HEAVY penalty for short locations (they match everything but are less specific)
  // A location with only 2-3 tokens that matches them all is probably too generic
  if (meaningfulLocationTokens.length <= 2) {
    score *= 0.5; // 50% penalty
  } else if (meaningfulLocationTokens.length === 3) {
    score *= 0.7; // 30% penalty
  }

  // Bonus for locations with 4+ tokens where most match (these are the specific ones we want)
  if (meaningfulLocationTokens.length >= 4 && matchedCount >= 4) {
    score += 0.15;
  }

  return Math.min(score, 1);
};

/**
 * Extract baseIP from location data array
 */
const extractBaseIP = (location) => {
  if (!location?.data) return null;

  let data = location.data;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      return null;
    }
  }

  if (!Array.isArray(data)) return null;

  const baseIPEntry = data.find(d => d.name === 'baseIP');
  return baseIPEntry?.value || null;
};

/**
 * Extract lanIP from device config or name
 */
const extractDeviceLanIP = (device) => {
  if (!device) return null;

  // Try direct lanIP field first
  if (device.lanIP) return device.lanIP;

  // Try config object
  let config = device.config;
  if (typeof config === 'string') {
    try {
      config = JSON.parse(config);
    } catch {
      // ignore
    }
  }

  if (config?.lanIP) return config.lanIP;

  // Standard CARO devices: config.identifier.lanIP
  if (config?.identifier?.lanIP) return config.identifier.lanIP;

  // UniFi devices: config.identity.lanIP
  if (config?.identity?.lanIP) return config.identity.lanIP;

  // Hexnode devices: config.hexnodeInfo.ipAddress
  if (config?.hexnodeInfo?.ipAddress) return config.hexnodeInfo.ipAddress;

  // Try nested paths
  if (config?.network?.lanIP) return config.network.lanIP;
  if (config?.device?.lanIP) return config.device.lanIP;

  // Try to extract IP from device name (e.g., "Antenna 10.20.31.106")
  const name = device.friendlyName || device.name || '';
  const ipMatch = name.match(/\b(10\.\d{1,3}\.\d{1,3}\.\d{1,3})\b/);
  if (ipMatch) {
    return ipMatch[1];
  }

  return null;
};

/**
 * Find the best matching location for a device based on friendlyName and lanIP
 */
const findBestLocationMatch = (device, locations) => {
  if (!device?.friendlyName || !locations?.length) return null;

  let bestMatch = null;
  let bestScore = 0;

  // Extract device's LAN IP for matching
  const deviceLanIP = extractDeviceLanIP(device);

  // Debug: collect top matches to log
  const topMatches = [];

  for (const location of locations) {
    const locationName = location.friendlyName || location.name || '';
    let score = calculateSimilarity(device.friendlyName, locationName);
    let matchType = 'name';

    // Check for IP match - this is a strong indicator
    // Format: 10.[siteNumber].[vlan].[unit]
    // The VLAN (third octet) can differ, but siteNumber and unit should match
    const locationBaseIP = extractBaseIP(location);
    if (deviceLanIP && locationBaseIP) {
      const deviceParts = deviceLanIP.split('.');
      const locationParts = locationBaseIP.split('.');

      if (deviceParts.length === 4 && locationParts.length === 4) {
        // Match: 10.[siteNumber].[any vlan].[unit]
        // parts[0] = 10 (network)
        // parts[1] = siteNumber (must match)
        // parts[2] = vlan (can differ)
        // parts[3] = unit (must match)
        const siteMatches = deviceParts[1] === locationParts[1];
        const unitMatches = deviceParts[3] === locationParts[3];

        if (siteMatches && unitMatches) {
          // IP match! This is a very strong signal
          score = Math.max(score, 0.95); // At least 95% if IP matches
          matchType = 'ip';
          console.log(`[DeviceLinking] IP match found: device "${device.friendlyName}" (${deviceLanIP}) -> location "${locationName}" (${locationBaseIP}) [site:${deviceParts[1]}, unit:${deviceParts[3]}]`);
        }
      }
    }

    // Collect top scoring locations for debugging (only show baseIP if it's an IP match)
    if (score >= 0.5) {
      topMatches.push({
        name: locationName,
        score,
        matchType,
        baseIP: matchType === 'ip' ? locationBaseIP : null
      });
    }

    if (score > bestScore && score >= 0.3) { // Minimum threshold of 30%
      bestScore = score;
      bestMatch = {
        location,
        score,
        confidence: score >= 0.9 ? 'high' : score >= 0.7 ? 'high' : score >= 0.5 ? 'medium' : 'low',
        matchType,
        deviceLanIP,
        locationBaseIP
      };
    }
  }

  // Debug log for troubleshooting matching issues
  if (topMatches.length > 0) {
    topMatches.sort((a, b) => b.score - a.score);
    console.log(`[DeviceLinking] Match candidates for "${device.friendlyName}"${deviceLanIP ? ` (LAN: ${deviceLanIP})` : ''}:`,
      topMatches.slice(0, 5).map(m => `${m.name} (${Math.round(m.score * 100)}%${m.matchType === 'ip' ? ` IP:${m.baseIP}` : ''})`).join(', ')
    );

    // Also log all locations that contain the site code to help debug missing locations
    const siteMatch = device.friendlyName.match(/SH\d+/i);
    if (siteMatch) {
      const siteCode = siteMatch[0].toLowerCase();
      const relatedLocations = locations
        .map(l => (l.friendlyName || l.name || '').toLowerCase())
        .filter(n => n.includes(siteCode))
        .slice(0, 20);
      console.log(`[DeviceLinking] All locations containing "${siteCode}":`, relatedLocations.join(', '));
    }
  }

  return bestMatch;
};

const DeviceLinking = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const { t } = useThemeSettings();

  const [unlinkedDevices, setUnlinkedDevices] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [linking, setLinking] = useState(false);
  const [suggestions, setSuggestions] = useState({});
  const [processingAll, setProcessingAll] = useState(false);
  const [locationTypeFilter, setLocationTypeFilter] = useState('units'); // 'sites' or 'units'

  // Multi-select and batch processing state
  const [selectedDeviceIds, setSelectedDeviceIds] = useState(new Set());
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [batchToLocationOpen, setBatchToLocationOpen] = useState(false);
  const [batchTargetLocation, setBatchTargetLocation] = useState(null);
  const [batchProgress, setBatchProgress] = useState({
    isProcessing: false,
    current: 0,
    total: 0,
    currentDevice: null,
    results: [], // { device, location, success, error }
    startTime: null
  });

  const isAdmin = user?.role === 'admin';

  // Filter locations based on type (sites = no parent, units = has parent and is not a floor)
  const filteredLocations = useMemo(() => {
    if (!locations.length) return [];

    // Debug: Check if F05 locations exist in raw locations
    const f05Locations = locations.filter(loc => {
      const name = (loc.friendlyName || loc.name || '').toLowerCase();
      return name.includes('f05') || name.includes('f5');
    });
    if (f05Locations.length > 0) {
      console.log(`[DeviceLinking] F05 locations in RAW data:`, f05Locations.slice(0, 5).map(l => ({
        name: l.friendlyName || l.name,
        hasParent: !!(l.parentLocation || l.parent_id || l.parentId),
        locationType: l.locationType || l.locationTypeId || l.locationTypeModel?.id
      })));
    } else {
      console.log(`[DeviceLinking] NO F05 locations found in raw data! Total locations: ${locations.length}`);
    }

    if (locationTypeFilter === 'sites') {
      // Sites are top-level locations (no parent, or locationType = 1)
      return locations.filter(loc => {
        const hasNoParent = !loc.parentLocation && !loc.parent_id && !loc.parentId;
        const isSiteType = loc.locationType === 1 || loc.locationTypeId === 1 || loc.locationTypeModel?.id === 1;
        return hasNoParent || isSiteType;
      });
    } else {
      // Units are the most specific locations (rooms, not floors or buildings)
      const filtered = locations.filter(loc => {
        const name = (loc.friendlyName || loc.name || '').toLowerCase();

        // Exclude floors - they end in -Fxx pattern (e.g., SH11-14-F05)
        // A floor pattern is: ends with -F followed by digits, with nothing after
        const isFloor = /[-]f\d{1,2}$/i.test(name);

        // Exclude buildings - they're typically just site + building number (e.g., SH11-14)
        // A building pattern is: site code + building number only (2-3 parts)
        const parts = name.split('-');
        const isBuilding = parts.length <= 2;

        // Must have a parent to be a unit
        const hasParent = loc.parentLocation || loc.parent_id || loc.parentId;

        // Location type checks
        const isNotSiteType = loc.locationType !== 1 && loc.locationTypeId !== 1 && loc.locationTypeModel?.id !== 1;

        // Debug: log why F05 locations are filtered out
        if (name.includes('f05') || name.includes('f5')) {
          console.log(`[DeviceLinking] F05 location "${name}" filter check:`, {
            hasParent,
            isNotSiteType,
            isFloor,
            isBuilding,
            parts: parts.length,
            willInclude: hasParent && isNotSiteType && !isFloor && !isBuilding
          });
        }

        // A unit is: has parent, is not a site, is not a floor, is not just a building
        return hasParent && isNotSiteType && !isFloor && !isBuilding;
      });

      return filtered;
    }
  }, [locations, locationTypeFilter]);

  // Load unlinked devices
  const loadUnlinkedDevices = useCallback(async () => {
    try {
      setLoading(true);
      const response = await DeviceService.getAllDevices({ forceRefresh: true });
      const allDevices = response?.data?.data || [];

      // Filter to only unlinked devices (locationId is null, undefined, or 1)
      const unlinked = allDevices.filter(device => {
        // Check all possible location field variations
        const locationId = device.locationId ?? device.location_id ?? device.location ?? null;

        // Also check if there's a populated locationModel
        const locationModelId = device.locationModel?.id ?? device.locationModel?._id ?? null;

        // Device is unlinked if:
        // - locationId is null/undefined/0/empty OR equals 1
        // - AND locationModel is not populated or its id is 1
        const locationIdUnlinked = !locationId || locationId === 1 || locationId === '1' || locationId === 0;
        const locationModelUnlinked = !locationModelId || locationModelId === 1 || locationModelId === '1';

        return locationIdUnlinked && locationModelUnlinked;
      });

      setUnlinkedDevices(unlinked);
      console.log(`[DeviceLinking] Found ${unlinked.length} unlinked devices out of ${allDevices.length} total`);
    } catch (error) {
      console.error('[DeviceLinking] Error loading devices:', error);
      enqueueSnackbar(
        t ? t('DeviceLinking.error_loading_devices') : 'Failed to load devices',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, t]);

  // Load all locations
  const loadLocations = useCallback(async () => {
    try {
      setLoadingLocations(true);
      const response = await LocationService.getAllLocations({ forceRefresh: true });
      const allLocations = response?.data?.data || response?.data?.locations || response?.data || [];

      // Filter out location with id 1 (unassigned/default)
      const validLocations = Array.isArray(allLocations)
        ? allLocations.filter(loc => {
            const locId = loc.id || loc._id;
            return locId !== 1 && locId !== '1';
          })
        : [];

      setLocations(validLocations);
      console.log(`[DeviceLinking] Loaded ${validLocations.length} locations`);
    } catch (error) {
      console.error('[DeviceLinking] Error loading locations:', error);
      enqueueSnackbar(
        t ? t('DeviceLinking.error_loading_locations') : 'Failed to load locations',
        { variant: 'error' }
      );
    } finally {
      setLoadingLocations(false);
    }
  }, [enqueueSnackbar, t]);

  // Calculate suggestions when devices and filtered locations are loaded
  useEffect(() => {
    if (unlinkedDevices.length > 0 && filteredLocations.length > 0) {
      const newSuggestions = {};
      for (const device of unlinkedDevices) {
        const match = findBestLocationMatch(device, filteredLocations);
        if (match) {
          const deviceId = device.id || device._id || device.device_UUID;
          newSuggestions[deviceId] = match;
        }
      }
      setSuggestions(newSuggestions);
      console.log(`[DeviceLinking] Generated ${Object.keys(newSuggestions).length} suggestions for ${locationTypeFilter}`);
    }
  }, [unlinkedDevices, filteredLocations, locationTypeFilter]);

  // Initial load
  useEffect(() => {
    if (isAdmin) {
      loadUnlinkedDevices();
      loadLocations();
    }
  }, [isAdmin, loadUnlinkedDevices, loadLocations]);

  // Filter devices by search term
  const filteredDevices = useMemo(() => {
    if (!searchTerm) return unlinkedDevices;
    const term = searchTerm.toLowerCase();
    return unlinkedDevices.filter(device => {
      const name = (device.friendlyName || device.name || '').toLowerCase();
      const uuid = (device.device_UUID || '').toLowerCase();
      return name.includes(term) || uuid.includes(term);
    });
  }, [unlinkedDevices, searchTerm]);

  // Handle device selection
  const handleDeviceSelect = (device) => {
    setSelectedDevice(device);
    const deviceId = device.id || device._id || device.device_UUID;
    if (suggestions[deviceId]) {
      setSelectedLocation(suggestions[deviceId].location);
    } else {
      setSelectedLocation(null);
    }
  };

  // Link device to location
  const handleLinkDevice = async (device, location) => {
    if (!device || !location) return;

    const deviceId = device.id || device._id;
    const locationId = location.id || location._id;

    try {
      setLinking(true);
      // API expects 'deviceLocation' not 'locationId'
      await devicesApi.update(deviceId, { deviceLocation: locationId });

      enqueueSnackbar(
        t ? t('DeviceLinking.device_linked_successfully') : `Device "${device.friendlyName}" linked to "${location.friendlyName}"`,
        { variant: 'success' }
      );

      // Remove device from list
      setUnlinkedDevices(prev => prev.filter(d => (d.id || d._id) !== deviceId));

      // Clear selection if it was the linked device
      if (selectedDevice && (selectedDevice.id || selectedDevice._id) === deviceId) {
        setSelectedDevice(null);
        setSelectedLocation(null);
      }

      // Invalidate device cache
      DeviceService.invalidateCache();
    } catch (error) {
      console.error('[DeviceLinking] Error linking device:', error);
      enqueueSnackbar(
        t ? t('DeviceLinking.error_linking_device') : 'Failed to link device',
        { variant: 'error' }
      );
    } finally {
      setLinking(false);
    }
  };

  // Accept suggestion for a device
  const handleAcceptSuggestion = async (device) => {
    const deviceId = device.id || device._id || device.device_UUID;
    const suggestion = suggestions[deviceId];
    if (suggestion) {
      await handleLinkDevice(device, suggestion.location);
    }
  };

  // Skip device (just deselect it)
  const handleSkipDevice = () => {
    setSelectedDevice(null);
    setSelectedLocation(null);
  };

  // Refresh data
  const handleRefresh = () => {
    loadUnlinkedDevices();
    loadLocations();
  };

  // Multi-select handlers
  const handleToggleDeviceSelection = (deviceId, e) => {
    e?.stopPropagation();
    setSelectedDeviceIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(deviceId)) {
        newSet.delete(deviceId);
      } else {
        newSet.add(deviceId);
      }
      return newSet;
    });
  };

  const handleSelectAllWithSuggestions = () => {
    const devicesWithSuggestions = filteredDevices.filter(device => {
      const deviceId = device.id || device._id || device.device_UUID;
      return suggestions[deviceId];
    });
    const ids = devicesWithSuggestions.map(d => d.id || d._id || d.device_UUID);
    setSelectedDeviceIds(new Set(ids));
  };

  const handleClearSelection = () => {
    setSelectedDeviceIds(new Set());
  };

  // Get devices that are selected AND have suggestions
  const selectedDevicesWithSuggestions = useMemo(() => {
    return filteredDevices.filter(device => {
      const deviceId = device.id || device._id || device.device_UUID;
      return selectedDeviceIds.has(deviceId) && suggestions[deviceId];
    });
  }, [filteredDevices, selectedDeviceIds, suggestions]);

  // Get ALL selected devices (regardless of suggestions)
  const allSelectedDevices = useMemo(() => {
    return filteredDevices.filter(device => {
      const deviceId = device.id || device._id || device.device_UUID;
      return selectedDeviceIds.has(deviceId);
    });
  }, [filteredDevices, selectedDeviceIds]);

  // Batch link all selected devices to a manually chosen location
  const handleBatchLinkToLocation = async () => {
    if (allSelectedDevices.length === 0 || !batchTargetLocation) return;

    setBatchToLocationOpen(false);
    setBatchDialogOpen(true);
    setBatchProgress({
      isProcessing: true,
      current: 0,
      total: allSelectedDevices.length,
      currentDevice: null,
      results: [],
      startTime: Date.now()
    });

    const results = [];
    const linkedDeviceIds = [];
    const locationId = batchTargetLocation.id || batchTargetLocation._id;

    for (let i = 0; i < allSelectedDevices.length; i++) {
      const device = allSelectedDevices[i];
      const deviceId = device.id || device._id;

      setBatchProgress(prev => ({
        ...prev,
        current: i + 1,
        currentDevice: device
      }));

      try {
        await devicesApi.update(deviceId, { deviceLocation: locationId });
        results.push({ device, location: batchTargetLocation, success: true, error: null });
        linkedDeviceIds.push(deviceId);
      } catch (error) {
        console.error(`[DeviceLinking] Failed to link device ${device.friendlyName}:`, error);
        results.push({ device, location: batchTargetLocation, success: false, error: error.message || 'Unknown error' });
      }

      setBatchProgress(prev => ({ ...prev, results: [...results] }));

      if (i < allSelectedDevices.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    }

    setBatchProgress(prev => ({ ...prev, isProcessing: false, currentDevice: null }));

    if (linkedDeviceIds.length > 0) {
      setUnlinkedDevices(prev => prev.filter(d => !linkedDeviceIds.includes(d.id || d._id)));
      setSelectedDeviceIds(new Set());
      DeviceService.invalidateCache();
    }

    setBatchTargetLocation(null);

    const successCount = results.filter(r => r.success).length;
    const failCount = results.filter(r => !r.success).length;
    enqueueSnackbar(
      `Batch complete: ${successCount} linked to "${batchTargetLocation.friendlyName}"${failCount > 0 ? `, ${failCount} failed` : ''}`,
      { variant: failCount > 0 ? 'warning' : 'success' }
    );
  };

  // Batch link handler (suggestions-based)
  const handleBatchLink = async () => {
    if (selectedDevicesWithSuggestions.length === 0) return;

    setBatchDialogOpen(true);
    setBatchProgress({
      isProcessing: true,
      current: 0,
      total: selectedDevicesWithSuggestions.length,
      currentDevice: null,
      results: [],
      startTime: Date.now()
    });

    const results = [];
    const linkedDeviceIds = [];

    for (let i = 0; i < selectedDevicesWithSuggestions.length; i++) {
      const device = selectedDevicesWithSuggestions[i];
      const deviceId = device.id || device._id;
      const suggestion = suggestions[device.id || device._id || device.device_UUID];
      const location = suggestion?.location;

      setBatchProgress(prev => ({
        ...prev,
        current: i + 1,
        currentDevice: device
      }));

      try {
        const locationId = location.id || location._id;
        await devicesApi.update(deviceId, { deviceLocation: locationId });

        results.push({
          device,
          location,
          success: true,
          error: null
        });
        linkedDeviceIds.push(deviceId);
      } catch (error) {
        console.error(`[DeviceLinking] Failed to link device ${device.friendlyName}:`, error);
        results.push({
          device,
          location,
          success: false,
          error: error.message || 'Unknown error'
        });
      }

      setBatchProgress(prev => ({
        ...prev,
        results: [...results]
      }));

      // Small delay between API calls to prevent overwhelming the server
      if (i < selectedDevicesWithSuggestions.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    }

    // Update state after batch completes
    setBatchProgress(prev => ({
      ...prev,
      isProcessing: false,
      currentDevice: null
    }));

    // Remove linked devices from the list
    if (linkedDeviceIds.length > 0) {
      setUnlinkedDevices(prev => prev.filter(d => !linkedDeviceIds.includes(d.id || d._id)));
      setSelectedDeviceIds(new Set());
      DeviceService.invalidateCache();
    }

    const successCount = results.filter(r => r.success).length;
    const failCount = results.filter(r => !r.success).length;

    enqueueSnackbar(
      `Batch complete: ${successCount} linked${failCount > 0 ? `, ${failCount} failed` : ''}`,
      { variant: failCount > 0 ? 'warning' : 'success' }
    );
  };

  const handleCloseBatchDialog = () => {
    if (!batchProgress.isProcessing) {
      setBatchDialogOpen(false);
      setBatchProgress({
        isProcessing: false,
        current: 0,
        total: 0,
        currentDevice: null,
        results: [],
        startTime: null
      });
    }
  };

  // Calculate estimated time remaining
  const getEstimatedTimeRemaining = () => {
    if (!batchProgress.startTime || batchProgress.current === 0) return null;
    const elapsed = Date.now() - batchProgress.startTime;
    const avgTimePerDevice = elapsed / batchProgress.current;
    const remaining = (batchProgress.total - batchProgress.current) * avgTimePerDevice;
    const seconds = Math.ceil(remaining / 1000);
    if (seconds < 60) return `~${seconds}s remaining`;
    return `~${Math.ceil(seconds / 60)}m remaining`;
  };

  // Get confidence color
  const getConfidenceColor = (confidence) => {
    switch (confidence) {
      case 'high': return 'success';
      case 'medium': return 'warning';
      case 'low': return 'error';
      default: return 'default';
    }
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {t ? t('DeviceLinking.admin_required') : 'You do not have permission to access this page. Admin access required.'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      {/* Left Sidebar - Unlinked Device List */}
      <Box
        sx={{
          width: 400,
          borderRight: 1,
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper'
        }}
      >
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <LinkIcon color="primary" />
            <Typography variant="h6">
              {t ? t('DeviceLinking.title') : 'Device Linking'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              onClick={handleRefresh}
              disabled={loading || loadingLocations}
              size="small"
              startIcon={<RefreshIcon />}
            >
              {t ? t('Common.refresh') : 'Refresh'}
            </Button>

            {/* Multi-select buttons */}
            {selectedDeviceIds.size > 0 ? (
              <>
                {selectedDevicesWithSuggestions.length > 0 && (
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleBatchLink}
                    disabled={linking}
                    size="small"
                    startIcon={<PlayIcon />}
                  >
                    Link {selectedDevicesWithSuggestions.length} Suggested
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setBatchToLocationOpen(true)}
                  disabled={linking}
                  size="small"
                  startIcon={<LocationIcon />}
                >
                  Link {selectedDeviceIds.size} to Location...
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleClearSelection}
                  size="small"
                  startIcon={<CloseIcon />}
                >
                  Clear
                </Button>
              </>
            ) : (
              <Button
                variant="outlined"
                onClick={handleSelectAllWithSuggestions}
                disabled={Object.keys(suggestions).length === 0}
                size="small"
                startIcon={<SelectAllIcon />}
              >
                Select All
              </Button>
            )}
          </Box>

          {/* Location Type Filter */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              {t ? t('DeviceLinking.suggest_for') : 'Suggest for:'}
            </Typography>
            <ToggleButtonGroup
              value={locationTypeFilter}
              exclusive
              onChange={(e, value) => value && setLocationTypeFilter(value)}
              size="small"
              fullWidth
            >
              <ToggleButton value="units">
                <UnitIcon sx={{ mr: 0.5, fontSize: 18 }} />
                {t ? t('DeviceLinking.units') : 'Units'}
              </ToggleButton>
              <ToggleButton value="sites">
                <SiteIcon sx={{ mr: 0.5, fontSize: 18 }} />
                {t ? t('DeviceLinking.sites') : 'Sites'}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <TextField
            fullWidth
            size="small"
            placeholder={t ? t('DeviceLinking.search_devices') : 'Search devices...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              )
            }}
          />
        </Box>

        {/* Stats */}
        <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}>
          <Typography variant="body2" color="text.secondary">
            {t ? t('DeviceLinking.unlinked_devices_count', { count: unlinkedDevices.length }) : `${unlinkedDevices.length} unlinked devices`}
            {Object.keys(suggestions).length > 0 && (
              <Chip
                size="small"
                icon={<SuggestionIcon />}
                label={t ? t('DeviceLinking.suggestions_count', { count: Object.keys(suggestions).length }) : `${Object.keys(suggestions).length} suggestions`}
                color="primary"
                variant="outlined"
                sx={{ ml: 1 }}
              />
            )}
          </Typography>
        </Box>

        {/* Device List */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ p: 2 }}>
              {[1, 2, 3, 4, 5].map(i => (
                <Skeleton key={i} variant="rectangular" height={60} sx={{ mb: 1, borderRadius: 1 }} />
              ))}
            </Box>
          ) : filteredDevices.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <CheckIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                {t ? t('DeviceLinking.all_devices_linked') : 'All devices are linked!'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t ? t('DeviceLinking.no_unlinked_devices') : 'No unlinked devices found.'}
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {filteredDevices.map((device) => {
                const deviceId = device.id || device._id || device.device_UUID;
                const suggestion = suggestions[deviceId];
                const isSelected = selectedDevice && (selectedDevice.id || selectedDevice._id) === (device.id || device._id);
                const isChecked = selectedDeviceIds.has(deviceId);

                return (
                  <ListItem
                    key={deviceId}
                    button
                    selected={isSelected}
                    onClick={() => handleDeviceSelect(device)}
                    sx={{
                      borderBottom: 1,
                      borderColor: 'divider',
                      '&:hover': { bgcolor: 'action.hover' },
                      bgcolor: isChecked ? 'action.selected' : undefined
                    }}
                  >
                    {/* Checkbox for multi-select */}
                    <Checkbox
                      checked={isChecked}
                      onChange={(e) => handleToggleDeviceSelection(deviceId, e)}
                      onClick={(e) => e.stopPropagation()}
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <DeviceIcon fontSize="small" color="action" />
                          <Typography variant="body1" noWrap>
                            {device.friendlyName || device.name || 'Unknown Device'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" component="span">
                            ({device.config_lan_ip || extractDeviceLanIP(device) || 'No LAN IP'})
                          </Typography>
                          {device.deviceTypeModel?.friendlyName && (
                            <Chip
                              size="small"
                              label={device.deviceTypeModel.friendlyName}
                              variant="outlined"
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          {suggestion ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                              <LocationIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary" component="span">
                                {suggestion.location.friendlyName}
                              </Typography>
                              <Chip
                                size="small"
                                label={`${Math.round(suggestion.score * 100)}%`}
                                color={getConfidenceColor(suggestion.confidence)}
                                sx={{ height: 18, fontSize: '0.65rem', ml: 0.5 }}
                              />
                              {suggestion.matchType === 'ip' && (
                                <Chip
                                  size="small"
                                  label={`IP: ${suggestion.locationBaseIP}`}
                                  color="info"
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: '0.65rem' }}
                                />
                              )}
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.disabled" component="div">
                              {t ? t('DeviceLinking.no_suggestion') : 'No suggestion found'}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    {suggestion && (
                      <ListItemSecondaryAction>
                        <Tooltip title={t ? t('DeviceLinking.accept_suggestion') : 'Accept suggestion'}>
                          <IconButton
                            edge="end"
                            size="small"
                            color="success"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAcceptSuggestion(device);
                            }}
                            disabled={linking}
                          >
                            <CheckIcon />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    )}
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>
      </Box>

      {/* Right Panel - Device Details & Location Selection */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {selectedDevice ? (
          <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <DeviceIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  {selectedDevice.friendlyName || selectedDevice.name || 'Unknown Device'}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t ? t('DeviceLinking.device_uuid') : 'Device UUID'}
                    </Typography>
                    <Typography variant="body2">
                      {selectedDevice.device_UUID || '-'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t ? t('DeviceLinking.device_type') : 'Device Type'}
                    </Typography>
                    <Typography variant="body2">
                      {selectedDevice.deviceTypeModel?.friendlyName || selectedDevice.deviceTypeId || '-'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t ? t('DeviceLinking.status') : 'Status'}
                    </Typography>
                    <Typography variant="body2">
                      <Chip
                        size="small"
                        label={selectedDevice.status || 'Unknown'}
                        color={selectedDevice.status === 'online' ? 'success' : 'default'}
                      />
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t ? t('DeviceLinking.last_seen') : 'Last Seen'}
                    </Typography>
                    <Typography variant="body2">
                      {selectedDevice.lastSeen
                        ? new Date(selectedDevice.lastSeen).toLocaleString()
                        : '-'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Suggestion Card */}
            {suggestions[selectedDevice.id || selectedDevice._id || selectedDevice.device_UUID] && (
              <Card sx={{ mb: 3, bgcolor: 'action.hover' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <SuggestionIcon color="primary" />
                    <Typography variant="h6">
                      {t ? t('DeviceLinking.suggested_location') : 'Suggested Location'}
                    </Typography>
                    <Chip
                      size="small"
                      label={`${Math.round(suggestions[selectedDevice.id || selectedDevice._id || selectedDevice.device_UUID].score * 100)}% match`}
                      color={getConfidenceColor(suggestions[selectedDevice.id || selectedDevice._id || selectedDevice.device_UUID].confidence)}
                    />
                  </Box>

                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationIcon color="action" />
                      <Typography variant="body1">
                        {suggestions[selectedDevice.id || selectedDevice._id || selectedDevice.device_UUID].location.friendlyName}
                      </Typography>
                      {suggestions[selectedDevice.id || selectedDevice._id || selectedDevice.device_UUID].matchType === 'ip' && (
                        <Chip
                          size="small"
                          label={`IP: ${suggestions[selectedDevice.id || selectedDevice._id || selectedDevice.device_UUID].locationBaseIP}`}
                          color="info"
                        />
                      )}
                    </Box>
                    {suggestions[selectedDevice.id || selectedDevice._id || selectedDevice.device_UUID].location.path && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
                        {suggestions[selectedDevice.id || selectedDevice._id || selectedDevice.device_UUID].location.path}
                      </Typography>
                    )}
                    {suggestions[selectedDevice.id || selectedDevice._id || selectedDevice.device_UUID].matchType === 'ip' && (
                      <Box sx={{ mt: 1, ml: 4 }}>
                        <Typography variant="caption" color="info.main">
                          Device LAN IP ({suggestions[selectedDevice.id || selectedDevice._id || selectedDevice.device_UUID].deviceLanIP}) matches location baseIP
                        </Typography>
                      </Box>
                    )}
                  </Paper>

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<CheckIcon />}
                      onClick={() => handleAcceptSuggestion(selectedDevice)}
                      disabled={linking}
                    >
                      {t ? t('DeviceLinking.accept') : 'Accept'}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CloseIcon />}
                      onClick={handleSkipDevice}
                    >
                      {t ? t('DeviceLinking.skip') : 'Skip'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Manual Location Selection */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <LocationIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  {t ? t('DeviceLinking.select_location') : 'Select Location'}
                </Typography>

                <Autocomplete
                  options={locations}
                  getOptionLabel={(option) => option.friendlyName || option.name || ''}
                  value={selectedLocation}
                  onChange={(e, value) => setSelectedLocation(value)}
                  loading={loadingLocations}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t ? t('DeviceLinking.location') : 'Location'}
                      placeholder={t ? t('DeviceLinking.search_location') : 'Search for a location...'}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLocations ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id || option._id}>
                      <Box>
                        <Typography variant="body1">
                          {option.friendlyName || option.name}
                        </Typography>
                        {option.path && (
                          <Typography variant="caption" color="text.secondary">
                            {option.path}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  sx={{ mb: 2 }}
                />

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<LinkIcon />}
                    onClick={() => handleLinkDevice(selectedDevice, selectedLocation)}
                    disabled={!selectedLocation || linking}
                  >
                    {linking ? <CircularProgress size={24} /> : (t ? t('DeviceLinking.link_device') : 'Link Device')}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleSkipDevice}
                  >
                    {t ? t('Common.cancel') : 'Cancel'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              p: 3
            }}
          >
            <LinkIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t ? t('DeviceLinking.select_device') : 'Select a device'}
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              {t ? t('DeviceLinking.select_device_description') : 'Choose a device from the list to link it to a location'}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Batch Processing Dialog */}
      <Dialog
        open={batchDialogOpen}
        onClose={handleCloseBatchDialog}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={batchProgress.isProcessing}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {batchProgress.isProcessing ? (
              <CircularProgress size={24} />
            ) : batchProgress.results.some(r => !r.success) ? (
              <WarningIcon color="warning" />
            ) : (
              <SuccessIcon color="success" />
            )}
            <Typography variant="h6">
              {batchProgress.isProcessing ? 'Linking Devices...' : 'Batch Complete'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {/* Progress Bar */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Progress: {batchProgress.current} of {batchProgress.total}
              </Typography>
              {batchProgress.isProcessing && getEstimatedTimeRemaining() && (
                <Typography variant="body2" color="text.secondary">
                  {getEstimatedTimeRemaining()}
                </Typography>
              )}
            </Box>
            <LinearProgress
              variant="determinate"
              value={batchProgress.total > 0 ? (batchProgress.current / batchProgress.total) * 100 : 0}
              sx={{ height: 8, borderRadius: 1 }}
            />
          </Box>

          {/* Current Device */}
          {batchProgress.isProcessing && batchProgress.currentDevice && (
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                Currently processing:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body1">
                  {batchProgress.currentDevice.friendlyName || batchProgress.currentDevice.name}
                </Typography>
              </Box>
            </Paper>
          )}

          {/* Results List */}
          {batchProgress.results.length > 0 && (
            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
              <Typography variant="subtitle2" gutterBottom>
                Results:
              </Typography>
              <List dense disablePadding>
                {batchProgress.results.map((result, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      bgcolor: result.success ? 'success.main' : 'error.main',
                      color: 'white',
                      borderRadius: 1,
                      mb: 0.5,
                      py: 0.5
                    }}
                  >
                    {result.success ? (
                      <SuccessIcon sx={{ mr: 1, fontSize: 18 }} />
                    ) : (
                      <ErrorIcon sx={{ mr: 1, fontSize: 18 }} />
                    )}
                    <ListItemText
                      primary={
                        <Typography variant="body2" noWrap>
                          {result.device.friendlyName || result.device.name}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }} noWrap>
                          {result.success
                            ? `→ ${result.location?.friendlyName || 'Unknown'}`
                            : result.error
                          }
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Summary when complete */}
          {!batchProgress.isProcessing && batchProgress.results.length > 0 && (
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SuccessIcon color="success" />
                  <Typography variant="body1">
                    {batchProgress.results.filter(r => r.success).length} successful
                  </Typography>
                </Box>
                {batchProgress.results.some(r => !r.success) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ErrorIcon color="error" />
                    <Typography variant="body1">
                      {batchProgress.results.filter(r => !r.success).length} failed
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseBatchDialog}
            disabled={batchProgress.isProcessing}
            variant={batchProgress.isProcessing ? 'text' : 'contained'}
          >
            {batchProgress.isProcessing ? 'Processing...' : 'Close'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Batch Link to Location Dialog */}
      <Dialog
        open={batchToLocationOpen}
        onClose={() => { setBatchToLocationOpen(false); setBatchTargetLocation(null); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationIcon color="primary" />
            Link {selectedDeviceIds.size} Devices to Location
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            All {selectedDeviceIds.size} selected devices will be linked to the chosen location.
          </Typography>

          <Autocomplete
            options={locations}
            getOptionLabel={(option) => option.friendlyName || option.name || ''}
            value={batchTargetLocation}
            onChange={(e, value) => setBatchTargetLocation(value)}
            loading={loadingLocations}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Target Location"
                placeholder="Search for a location..."
                autoFocus
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingLocations ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id || option._id}>
                <Box>
                  <Typography variant="body1">
                    {option.friendlyName || option.name}
                  </Typography>
                  {option.path && (
                    <Typography variant="caption" color="text.secondary">
                      {option.path}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            sx={{ mb: 2 }}
          />

          {batchTargetLocation && (
            <Alert severity="info">
              <Typography variant="body2">
                <strong>{selectedDeviceIds.size}</strong> devices will be linked to <strong>"{batchTargetLocation.friendlyName}"</strong>
              </Typography>
            </Alert>
          )}

          {/* Preview of selected devices */}
          {allSelectedDevices.length > 0 && (
            <Box sx={{ mt: 2, maxHeight: 200, overflow: 'auto' }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Selected devices:
              </Typography>
              {allSelectedDevices.map(device => (
                <Chip
                  key={device.id || device._id}
                  label={device.friendlyName || device.name || 'Unknown'}
                  size="small"
                  sx={{ mr: 0.5, mb: 0.5 }}
                  onDelete={() => {
                    const deviceId = device.id || device._id || device.device_UUID;
                    setSelectedDeviceIds(prev => {
                      const newSet = new Set(prev);
                      newSet.delete(deviceId);
                      return newSet;
                    });
                  }}
                />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setBatchToLocationOpen(false); setBatchTargetLocation(null); }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleBatchLinkToLocation}
            disabled={!batchTargetLocation}
            startIcon={<LinkIcon />}
          >
            Link {selectedDeviceIds.size} Devices
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeviceLinking;
